import { supabase } from '../config/supabase';
import { isManualUploadedMedicalRecord, isMedicalRecordInvalidated } from '../utils/medicalRecordStatus';

interface MedicalRecord {
  id: string;
  name: string;
  url: string;
  uploadDate: Date;
  patientName: string;
  doctorName: string;
  patientId: string;
  doctorId: string;
  clientId?: string;
  clientName?: string;
  metadata: any;
}

const extractAttachmentUrl = (attachment: any): string => {
  if (!attachment) return "";

  if (typeof attachment === "string") {
    return attachment;
  }

  const pickUrlFromObject = (obj: any): string => {
    if (!obj || typeof obj !== "object") return "";
    return obj.url || obj.fileUrl || obj.downloadUrl || obj.signedUrl || "";
  };

  if (Array.isArray(attachment)) {
    for (const item of attachment) {
      if (typeof item === "string" && item) return item;
      const url = pickUrlFromObject(item);
      if (url) return url;
    }
    return "";
  }

  const directUrl = pickUrlFromObject(attachment);
  if (directUrl) return directUrl;

  if (Array.isArray(attachment.attachments)) {
    for (const item of attachment.attachments) {
      if (typeof item === "string" && item) return item;
      const url = pickUrlFromObject(item);
      if (url) return url;
    }
  }

  return "";
};

export const getMedicalRecords = async (userId: string, userRole: string, clientId?: string): Promise<MedicalRecord[]> => {
  try {
    if (!clientId) {
      return [];
    }

    let queryBuilder = supabase
      .from("medical_records")
      .select("*")
      .eq("clientId", clientId)
      .order("recordDate", { ascending: false });

    if (userRole === "doctor" || userRole === "fisioterapeuta") {
      queryBuilder = queryBuilder.eq("doctorId", userId);
    } else if (userRole === "patient") {
      const { data: patientData, error: patientError } = await supabase
        .from("patients")
        .select(`
          patientId,
          patient_clients!inner(clientId)
        `)
        .eq("userId", userId)
        .eq("patient_clients.clientId", clientId)
        .maybeSingle();

      if (patientError) {
        console.error("Error resolving patient record by tenant:", patientError);
        throw patientError;
      }

      if (!patientData?.patientId) {
        return [];
      }

      queryBuilder = queryBuilder.eq("patientId", patientData.patientId);
    }

    const { data, error } = await queryBuilder;

    if (error) {
      console.error("Error fetching medical records:", error);
      throw error;
    }

    // Mapear os dados do Supabase para o formato MedicalRecord
    return (data || [])
      .filter((record: any) =>
        !isMedicalRecordInvalidated(record.recordType, record.recordAttachments) &&
        isManualUploadedMedicalRecord(record.recordAttachments)
      )
      .map((record: any) => ({
        id: record.recordId,
        name: record.recordTitle,
        url: extractAttachmentUrl(record.recordAttachments),
        uploadDate: new Date(record.recordCreatedAt),
        patientName: record.patientName || "", // Você pode precisar buscar o nome do paciente de outra forma
        doctorName: record.doctorName || "", // Você pode precisar buscar o nome do doutor de outra forma
        patientId: record.patientId,
        doctorId: record.doctorId,
        metadata: record.recordAttachments, // Ou outros metadados relevantes
      }));

  } catch (error) {
    console.error("Failed to get medical records:", error);
    throw error;
  }
};

export const getPatientMedicalRecordsAcrossClients = async (
  patientId: string,
  consentedClientIds: string[],
): Promise<MedicalRecord[]> => {
  try {
    if (!patientId || consentedClientIds.length === 0) {
      return [];
    }

    let queryBuilder = supabase
      .from("medical_records")
      .select(`
        recordId,
        recordTitle,
        recordType,
        recordDate,
        recordCreatedAt,
        recordAttachments,
        patientId,
        doctorId,
        clientId,
        users(userDisplayName),
        patients(patientFullName),
        clients(clientName)
      `)
      .eq("patientId", patientId)
      .in("clientId", consentedClientIds)
      .order("recordDate", { ascending: false });

    const { data, error } = await queryBuilder;

    if (error) {
      console.error("Error fetching consolidated patient medical records:", error);
      throw error;
    }

    return (data || [])
      .filter((record: any) =>
        !isMedicalRecordInvalidated(record.recordType, record.recordAttachments) &&
        isManualUploadedMedicalRecord(record.recordAttachments)
      )
      .map((record: any) => {
        const doctorData = Array.isArray(record.users) ? record.users[0] : record.users;
        const patientData = Array.isArray(record.patients) ? record.patients[0] : record.patients;
        const clientData = Array.isArray(record.clients) ? record.clients[0] : record.clients;
        const attachment = record.recordAttachments;
        const resolvedUrl = extractAttachmentUrl(attachment);

        return {
          id: record.recordId,
          name: record.recordTitle,
          url: resolvedUrl,
          uploadDate: new Date(record.recordCreatedAt || record.recordDate),
          patientName: patientData?.patientFullName || "",
          doctorName: doctorData?.userDisplayName || "",
          patientId: record.patientId,
          doctorId: record.doctorId,
          clientId: record.clientId,
          clientName: clientData?.clientName || "",
          metadata: attachment,
        } as MedicalRecord;
      });
  } catch (error) {
    console.error("Failed to get consolidated patient medical records:", error);
    throw error;
  }
};

export interface MedicalRecordInput {
  title: string;
  description: string;
  patientId: string;
  doctorId: string;
  type: string;
  date: Date;
  attachments: Array<{
    name: string;
    url: string;
    path?: string;
    provider?: string;
    downloadUrl?: string;
    type: string;
  }>;
  uploadedBy: string | undefined;
  uploadedByRole: string | undefined;
  uploadedByName: string;
  clientId: string; // Adicionado clientId
}

export async function createMedicalRecord(input: MedicalRecordInput) {
  const { 
    title, 
    description, 
    patientId, 
    doctorId, 
    type, 
    date, 
    attachments, 
    uploadedBy, 
    uploadedByRole, 
    uploadedByName,
    clientId
  } = input;

  if (!clientId) {
    throw new Error("ClientId é obrigatório para criar registro médico.");
  }

  const [{ data: scopedPatient, error: scopedPatientError }, { data: scopedDoctor, error: scopedDoctorError }] = await Promise.all([
    supabase
      .from("patient_clients")
      .select("patientId")
      .eq("patientId", patientId)
      .eq("clientId", clientId)
      .maybeSingle(),
    supabase
      .from("user_clients")
      .select("userId, userRole")
      .eq("userId", doctorId)
      .eq("clientId", clientId)
      .in("userRole", ["doctor", "fisioterapeuta", "admin", "interno"])
      .maybeSingle()
  ]);

  if (scopedPatientError) {
    console.error("Erro ao validar paciente no tenant:", scopedPatientError);
    throw scopedPatientError;
  }

  if (!scopedPatient) {
    throw new Error("Paciente não pertence ao tenant selecionado.");
  }

  if (scopedDoctorError) {
    console.error("Erro ao validar profissional no tenant:", scopedDoctorError);
    throw scopedDoctorError;
  }

  if (!scopedDoctor) {
    throw new Error("Profissional não pertence ao tenant selecionado.");
  }

  const { data, error } = await supabase
    .from("medical_records")
    .insert({
      clientId: clientId,
      patientId: patientId,
      doctorId: doctorId, // No schema, doctorId é UUID, mas aqui estamos passando o userId do doctor
      recordType: type,
      recordDate: date.toISOString(),
      recordTitle: title,
      recordDescription: description,
      recordAttachments: attachments, // JSONB
      // Adicione aqui outros campos relevantes, como quem fez o upload, se necessário
      // No schema, doctorId é quem criou. Precisamos ver como mapear uploadedBy
    })
    .select(); // Retorna o registro criado

  if (error) {
    console.error("Erro ao criar registro médico no Supabase:", error);
    throw error;
  }

  return data[0];
}
